import { z } from "zod";
import { Id } from "../common/ids.js";
import { PageQuery, page } from "../common/pagination.js";
import { defineRoute } from "../http/route.js";
import { ProductDraft, ProductStatus, ProductSummary, ProductView } from "./listing.js";
/** The shop's products (CAT-3, CAT-6, card 037); submitting and publishing are card 038's. */
const Seller = { surface: "tenant", auth: "user", permission: "product:manage" };
const ProductId = z.object({ productId: Id });
export const listShopProducts = defineRoute({
    ...Seller,
    id: "catalog.products.list",
    method: "GET",
    path: "/v1/shop/products",
    marks: ["tenant"],
    summary: "The shop's products, most recently changed first, optionally in one status",
    prd: ["CAT-4"],
    request: { query: PageQuery.extend({ status: ProductStatus.optional() }) },
    responses: { 200: { description: "Products", schema: page(ProductSummary) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const createProduct = defineRoute({
    ...Seller,
    id: "catalog.products.create",
    method: "POST",
    path: "/v1/shop/products",
    marks: ["tenant", "idem"],
    summary: "Create a draft in a leaf category. Only a category, an English name, and availability are needed now; what is written must be right, and the rest is checked when it is submitted. Images are added once the draft exists, since they are uploaded for it",
    prd: ["CAT-3", "CAT-6"],
    request: {
        body: ProductDraft.omit({ media: true })
            .partial()
            .required({ categoryId: true, name: true, availability: true }),
    },
    responses: { 201: { description: "The draft", schema: ProductView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const getShopProduct = defineRoute({
    ...Seller,
    id: "catalog.products.get",
    method: "GET",
    path: "/v1/shop/products/{productId}",
    marks: ["tenant"],
    summary: "A product as it stands, with its pending revision if it is published and being edited",
    prd: ["CAT-3"],
    request: { params: ProductId },
    responses: { 200: { description: "The product", schema: ProductView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const updateProduct = defineRoute({
    ...Seller,
    id: "catalog.products.update",
    method: "PATCH",
    path: "/v1/shop/products/{productId}",
    marks: ["tenant", "if-match"],
    summary: "Edit a product; `skus` and `media` replace the lists. A draft changes at once. A live or off-shelf product keeps what the public sees, and the edits wait as its pending revision until submitted (card 038). A product in review or blocked cannot be edited",
    prd: ["CAT-3", "CAT-6"],
    request: { params: ProductId, body: ProductDraft.partial() },
    responses: { 200: { description: "The product", schema: ProductView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const deleteProduct = defineRoute({
    ...Seller,
    id: "catalog.products.delete",
    method: "DELETE",
    path: "/v1/shop/products/{productId}",
    marks: ["tenant"],
    summary: "Delete a draft. A product that was published or reviewed is kept",
    prd: ["CAT-4"],
    request: { params: ProductId },
    responses: { 204: { description: "Deleted" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const productRoutes = [
    listShopProducts,
    createProduct,
    getShopProduct,
    updateProduct,
    deleteProduct,
];
//# sourceMappingURL=product.routes.js.map